import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faMusic, faPodcast, faImage, faEye, faEyeSlash, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import './Media.css';

// Create Sanity client
const sanityClient = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID || process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || 'd8gvm4xl',
  dataset: process.env.REACT_APP_SANITY_DATASET || process.env.NEXT_PUBLIC_SANITY_DATASET || 'production',
  useCdn: false,
  apiVersion: '2023-01-01',
  token: process.env.SANITY_API_TOKEN // Add token for fresh data
});

const builder = imageUrlBuilder(sanityClient);
const urlFor = (source) => builder.image(source);

const MediaSanity = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Refs for sections
  const videoSectionRef = useRef(null);
  const audioSectionRef = useRef(null);
  const podcastSectionRef = useRef(null);
  const sitesSectionRef = useRef(null);
  const photoSectionRef = useRef(null);

  // Icon mapping for sections
  const sectionIcons = {
    'prm videos': faVideo,
    'audio': faMusic,
    'prm podcasts': faPodcast,
    'sites': faGlobe,
    'photo galleries': faImage
  };

  useEffect(() => {
    const loadMedia = async () => {
      try {
        // Fetch Media sections with their related cards (filtered by pageType)
        const query = `
          *[_type == "section" && pageType == "media"] | order(order asc) {
            _id,
            title,
            slug,
            order,
            "cards": *[_type == "card" && references(^._id) && pageType == "media"] | order(order asc) {
              _id,
              title,
              href,
              body,
              image,
              order
            }
          }
        `;
        
        // Add cache-busting parameter to force fresh data
        const data = await sanityClient.fetch(query, {}, {
          cache: 'no-store'
        });
        setSections(data);
      } catch (err) {
        setError('Failed to load media');
        console.error('Error loading media:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMedia();
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const limitCards = (cards, isExpanded) => {
    if (isExpanded || isMobile) return cards;
    return cards.slice(0, isMobile ? 2 : 4);
  };

  const getSectionIcon = (title) => {
    const key = title.toLowerCase();
    return sectionIcons[key] || faVideo;
  };



  if (loading) {
    return (
      <Container>
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0">
      {/* Media Header */}
      <div className="media-section main-bg py-5">
        <Container>
          <div className="text-center">
            <h1 className="section-title">Media</h1>
            <p className="section-subtitle">Explore PRM's in-house media including videos, music and other sites we partner with.</p>
          </div>
        </Container>
      </div>

      {/* Media Sections */}
      {sections.map((section, index) => {
        const isExpanded = expandedSections[section._id] || false;
        const displayedCards = limitCards(section.cards, isExpanded, isMobile);
        const hasMoreCards = section.cards.length > (isMobile ? 2 : 4);

        return (
          <div 
            key={section._id} 
            className={`media-section ${index % 2 === 0 ? 'card-bg' : 'main-bg'} py-5`}
            ref={section.slug?.current === 'prm-videos' ? videoSectionRef : 
                 section.slug?.current === 'audio' ? audioSectionRef :
                 section.slug?.current === 'prm-podcasts' ? podcastSectionRef :
                 section.slug?.current === 'sites' ? sitesSectionRef :
                 section.slug?.current === 'photo-galleries' ? photoSectionRef : null}
          >
            <Container>
              <Row className="mb-3">
                <Col>
                  <h2 className="h3 mb-2">
                    <FontAwesomeIcon icon={getSectionIcon(section.title)} className="me-2" />
                    {section.title}
                  </h2>
                  <p className="subtitle-text">
                    {section.title === 'PRM Videos' && 'Experience PRM\'s teachings, worship, and testimonies'}
                    {section.title === 'Audio' && 'Worship music and soaking tracks to enhance your prayer time'}
                    {section.title === 'PRM Podcasts' && 'Listen to our podcasts exploring glory concepts and interviews'}
                    {section.title === 'Sites' && 'Explore other sites we\'re part of'}
                    {section.title === 'Photo Galleries' && 'Browse through our collection of event and ministry photos'}
                  </p>
                </Col>
              </Row>
              <Row className="media-gallery">
                {displayedCards.map(card => (
                  <Col key={card._id} xs={6} md={3} className="mb-4">
                    <div className="media-card">
                      <a href={card.href} target="_blank" rel="noopener noreferrer">
                        {card.image ? (
                          <img src={urlFor(card.image).url()} alt={card.title} className="media-thumbnail-img" />
                        ) : (
                          <div className="placeholder-media">
                            <FontAwesomeIcon icon={getSectionIcon(section.title)} className="placeholder-icon" />
                          </div>
                        )}
                        <div className="media-title">{card.title}</div>
                      </a>
                    </div>
                  </Col>
                ))}
              </Row>
              {hasMoreCards && (
                <Row className="justify-content-center mt-3">
                  <Col xs="auto">
                    <Button variant="outline-primary" className="more-btn" onClick={() => toggleSection(section._id)}>
                      <FontAwesomeIcon icon={isExpanded ? faEyeSlash : faEye} className="me-2" />
                      {isExpanded ? "Show Less" : "Show More"}
                    </Button>
                  </Col>
                </Row>
              )}
            </Container>
          </div>
        );
      })}
    </Container>
  );
};

export default MediaSanity; 